import { createContext, useEffect, PropsWithChildren, useState, useRef, useCallback } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import mixpanel from 'mixpanel-browser';
import { getMixpanelToken, getMode } from '@/config/env';
import { isSyntheticSession } from '@/config/synthetic';
import { getGuestAddress } from '@/hooks/useGuestAddress';
import { getSurveySessionId } from '@/utils/surveySession';
import { zipFromAddressComponents } from '@/utils/analyticsZip';

const MIXPANEL_TOKEN = getMixpanelToken();
const MODE = getMode();

// Synthetic monitoring drives the real app every 30 minutes across production,
// stanford, and staging. Those runs land and immediately chat, so counting them
// puts ~144 scripted sessions a day into the same project as real voters and
// pulls any rate measured against landed users toward 100%. Leaving Mixpanel
// uninitialized is what keeps them out: it also suppresses autocaptured
// pageviews, which no per-call guard would catch.
const ANALYTICS_ENABLED = !isSyntheticSession();

const AUTOCAPTURE = {
  pageview: 'full-url',
  click: false,
  input: false,
  rage_click: false,
  scroll: false,
  submit: false,
} as const;

// The first pageview and Session Start wait for the app-derived super
// properties (auth state, a signed-in voter's profile ZIP), but never longer
// than this if auth stalls.
const PROPERTIES_READY_FALLBACK_MS = 5_000;

if (ANALYTICS_ENABLED) {
  if (!MIXPANEL_TOKEN) {
    throw new Error(
      'Mixpanel token missing. Double check token is in GitHub secrets or .env and retrieve as necessary.'
    );
  }

  // Super properties persist in localStorage, and init() sends the first
  // autocaptured pageview synchronously. Start with pageviews off and replace
  // the session-scoped properties a previous tab may have left behind;
  // pageviews start once SuperPropertiesSync has registered this visit's auth
  // state and ZIP (see enablePageviews).
  mixpanel.init(MIXPANEL_TOKEN, {
    debug: MODE === 'development',
    persistence: 'localStorage',
    autocapture: { ...AUTOCAPTURE, pageview: false },
  });
  mixpanel.register({
    survey_session_id: getSurveySessionId(),
    zip_code: zipFromAddressComponents(getGuestAddress()?.address_components),
  });
}

let pageviewsEnabled = false;

// Turning pageview autocapture on sends the first pageview, so it happens
// once, after the super properties are ready.
const enablePageviews = () => {
  if (!ANALYTICS_ENABLED || pageviewsEnabled) return;
  pageviewsEnabled = true;
  mixpanel.set_config({ autocapture: AUTOCAPTURE });
};

export interface AnalyticsContextValue {
  analytics: {
    track: typeof mixpanel.track;
    registerSuperProperties: (props: Record<string, unknown>) => void;
    // Called once the app-derived super properties are registered, so the
    // first pageview and Session Start carry them.
    markSuperPropertiesReady: () => void;
  };
}

export const AnalyticsContext = createContext<AnalyticsContextValue | null>(null);

// Unhandled errors are reported as 'Client Error' events, capped per page load
// so an error loop cannot flood the project.
const CLIENT_ERROR_CAP = 25;
const CLIENT_ERROR_MESSAGE_LIMIT = 200;

// Strip the values most likely to carry secrets or PII out of an error string
// before it is sent as an analytics property: URL query strings, email
// addresses, and long token-like blobs.
const scrubErrorMessage = (message: string): string =>
  message
    .replace(/\?[^\s'"]*/g, '?[redacted]')
    .replace(/[\w.+-]+@[\w-]+\.[\w.]+/g, '[email]')
    .replace(/[A-Za-z0-9_-]{24,}/g, '[token]')
    .slice(0, CLIENT_ERROR_MESSAGE_LIMIT);

export const AnalyticsProvider = ({ children }: PropsWithChildren) => {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isActive, setIsActive] = useState(false);
  const sessionStartTime = useRef<number | null>(null);
  const clientErrorCount = useRef(0);
  const propertiesReadyRef = useRef(false);

  const track = useCallback((...args: Parameters<typeof mixpanel.track>) => {
    if (!ANALYTICS_ENABLED) return;
    try {
      mixpanel.track(...args);
    } catch (error) {
      // TODO: queue failed events for later retry
      console.warn('mixpanel not ready or error occurred:', error);
    }
  }, []);

  const registerSuperProperties = useCallback((props: Record<string, unknown>) => {
    if (!ANALYTICS_ENABLED) return;
    try {
      mixpanel.register(props);
    } catch (error) {
      console.warn('mixpanel.register failed:', error);
    }
  }, []);

  // Start a new session
  const startSession = useCallback(() => {
    if (!propertiesReadyRef.current) return;
    const id = crypto.randomUUID();
    setSessionId(id);
    setIsActive(true);
    sessionStartTime.current = Date.now();
    track('Session Start', { sessionId: id });
  }, [track]);

  // End the current session
  const endSession = useCallback(() => {
    if (sessionId && sessionStartTime.current) {
      track(
        'Session End',
        {
          sessionId,
          duration: Date.now() - sessionStartTime.current,
        },
        { send_immediately: true }
      );
    }
    setIsActive(false);
    setSessionId(null);
    sessionStartTime.current = null;
  }, [sessionId, track]);

  // Idle detection
  useIdleTimer({
    timeout: 120_000, // 2min
    onIdle: endSession,
    onActive: () => {
      if (!isActive) startSession();
    },
    debounce: 500,
  });

  // Report unhandled errors and promise rejections as 'Client Error' so
  // failures between the user and the API are visible per platform, not just
  // in the browser console.
  useEffect(() => {
    const report = (source: string, raw: unknown) => {
      if (clientErrorCount.current >= CLIENT_ERROR_CAP) return;
      clientErrorCount.current += 1;
      const message = raw instanceof Error ? raw.message : String(raw ?? 'Unknown error');
      track('Client Error', {
        source,
        error_message: scrubErrorMessage(message),
      });
    };
    // Prefer the Error object over event.message so instanceof handling (and
    // any future stack capture) works; cross-origin scripts may only set message.
    const handleError = (event: ErrorEvent) => report('window_error', event.error ?? event.message);
    const handleRejection = (event: PromiseRejectionEvent) =>
      report('unhandled_rejection', event.reason);
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, [track]);

  // End session when user leaves
  useEffect(() => {
    const handleUnload = () => endSession();
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [endSession, sessionId]);

  const markSuperPropertiesReady = useCallback(() => {
    if (propertiesReadyRef.current) return;
    propertiesReadyRef.current = true;
    enablePageviews();
    startSession();
  }, [startSession]);

  // Start the session once properties are ready, or after the fallback.
  useEffect(() => {
    const timer = setTimeout(markSuperPropertiesReady, PROPERTIES_READY_FALLBACK_MS);
    return () => clearTimeout(timer);
  }, [markSuperPropertiesReady]);

  const value = {
    analytics: { track, registerSuperProperties, markSuperPropertiesReady },
  };

  return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>;
};
